import React, { useEffect, useState } from "react";
import { PartnerLayout } from "./PartnerLayout";
import partnerApi from "../../lib/partnerApi";
import { formatEUR } from "../../lib/api";
import { Lightbulb } from "lucide-react";

export default function PartnerAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    partnerApi.get("/partner/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <PartnerLayout title="Analytics"><div className="text-sm text-muted-foreground">Loading…</div></PartnerLayout>;

  const max = Math.max(1, ...data.funnel.map((s) => s.count));

  return (
    <PartnerLayout title="Analytics" subtitle="Conversion funnel, asset breakdown, and insights">
      <div className="xch-card p-6 mb-5" data-testid="funnel-chart">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">Conversion funnel</div>
        <div className="space-y-3">
          {data.funnel.map((s, i) => {
            const pct = (s.count / max) * 100;
            const prevCount = i > 0 ? data.funnel[i - 1].count : null;
            const dropPct = prevCount ? Math.round(((prevCount - s.count) / Math.max(prevCount, 1)) * 100) : null;
            return (
              <div key={s.stage} className="flex items-center gap-3" data-testid={`funnel-${i}`}>
                <div className="w-44 text-sm font-semibold text-foreground shrink-0">{s.stage}</div>
                <div className="flex-1 bg-muted rounded-full h-9 relative overflow-hidden">
                  <div className="bg-primary h-full rounded-full flex items-center px-3 transition-all" style={{ width: `${Math.max(pct, 8)}%` }}>
                    <span className="text-white font-mono font-bold text-sm tabular-nums">{s.count.toLocaleString()}</span>
                  </div>
                </div>
                <div className="w-20 text-right shrink-0">
                  {dropPct !== null && (
                    <span className={`text-xs font-mono tabular-nums ${dropPct > 50 ? "text-destructive" : dropPct > 25 ? "text-amber-600" : "text-muted-foreground"}`}>
                      -{dropPct}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {data.insight && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex gap-3" data-testid="insight">
          <Lightbulb className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-amber-900 text-sm">Top drop-off insight</div>
            <div className="text-sm text-amber-800 mt-0.5">{data.insight}</div>
          </div>
        </div>
      )}

      <div className="xch-card overflow-hidden" data-testid="breakdown-by-asset">
        <div className="p-4 border-b border-border">
          <div className="font-display text-base font-bold text-foreground">By asset</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b border-border">
              <Th>Asset</Th><Th right>Orders</Th><Th right>Volume</Th><Th right>Avg size</Th><Th right>Completion</Th>
            </tr></thead>
            <tbody>
              {data.by_asset.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No orders yet.</td></tr>
              )}
              {data.by_asset.map((a) => (
                <tr key={a.asset} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-display font-bold text-foreground">{a.asset}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{a.orders}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{formatEUR(a.volume_eur)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{formatEUR(a.avg_order_size_eur)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">{a.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PartnerLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);
